import Typography from '@components/v2/ui/Typography/Typography';
import { Box, CircularProgress } from '@mui/material';
import { useTranslation } from '../../../../modules/translation/TranslationContext';
import { useProgressStyles } from './progress.styles';
import { ProgressLabelColor, ProgressLabelVariant } from './progress.types';
import { formatProgressLabel, getProgressArc } from './progress.utils';

const RING_SIZE = 16;
const RING_STROKE = 1.6;
const MUI_VIEWBOX_SIZE = 44;
const RING_THICKNESS = (RING_STROKE * MUI_VIEWBOX_SIZE) / RING_SIZE;
const FULL_TRACK = 100;

interface ProgressProps {
  value: number;
  labelPosition?: 'left' | 'right';
  labelColor?: ProgressLabelColor;
  labelVariant?: ProgressLabelVariant;
  testId?: string;
}

const Progress = ({
  value,
  labelPosition = 'right',
  labelColor = 'text.primary',
  labelVariant = 'body1',
  testId,
}: ProgressProps) => {
  const { locale } = useTranslation();
  const { classes, cx } = useProgressStyles();

  const { arcValue, hasCompletedTurn } = getProgressArc(value);

  const label = (
    <Typography variant={labelVariant} color={labelColor} className={classes.label}>
      {formatProgressLabel(value, locale)}
    </Typography>
  );

  return (
    <Box className={classes.root} test-id={testId}>
      {labelPosition === 'left' && label}
      <Box className={classes.ring} aria-hidden>
        <CircularProgress
          variant="determinate"
          size={RING_SIZE}
          thickness={RING_THICKNESS}
          value={FULL_TRACK}
          className={cx(classes.track, hasCompletedTurn && classes.completedTrack)}
        />
        <CircularProgress
          variant="determinate"
          size={RING_SIZE}
          thickness={RING_THICKNESS}
          value={arcValue}
          className={cx(classes.arc, hasCompletedTurn && classes.excessArc)}
        />
      </Box>
      {labelPosition === 'right' && label}
    </Box>
  );
};

export default Progress;
